import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { DataManager, Visualisation, Toolbar, Chart, FilterBar } from 'wc-visualisation';
import { Icon, Popover, formatDate, FieldType, downloadCsv, arrayToCsv } from 'wc-lib';
import * as screenfull from 'screenfull';
import { NemRegions } from '../config';
import { insertMissingIntervals } from './dataUtils';
// import { FPP_RESIDUAL_CONTRIBUTION_FACTOR_testData } from './test-data/FPP_RESIDUAL_CONTRIBUTION_FACTOR-test-data';
// import { FPP_RESIDUAL_PERFORMANCE_testData } from './test-data/FPP_RESIDUAL_PERFORMANCE-test-data';

require('wc-lib/icons/downloadcircle.svg');
require('wc-lib/icons/fullscreen.svg');

@Component({
  template: require('./fpp-residual.html'),
  name: 'fpp-residual',
  components: {
    'vis-visualisation': Visualisation,
    'vis-toolbar': Toolbar,
    'wc-icon': Icon,
    'vis-chart': Chart,
    'vis-filter-bar': FilterBar,
    'wc-popover': Popover
  }
})
export class FppResidual extends Vue {
  contributionFactorTab = 'RESIDUAL_CONTRIBUTION_FACTOR';
  performanceTab = "RESIDUAL_PERFORMANCE";

  purple = '#B056BC';
  lightBlue = '#34B9B3';

  tabs = [{ value: this.performanceTab, label: 'Performance' }, { value: this.contributionFactorTab, label: 'Contribution Factor' }];
  selectedTab: string = this.tabs[0].value;

  constraintIds = [];
  selectedConstraintId: string = '';

  intervals = [{ value: '1', label: '1 hr' }, { value: '3', label: '3 hrs' }, { value: '24', label: '24 hrs' }]
  selectedInterval: string = this.intervals[0].value;

  regions: { label: string, value: string }[] = NemRegions;
  selectedRegionId: string = this.regions[0].value;

  loadMessage: string = '';
  dataCache: Record<string, any[]> = {};
  data: any[] = [];
  chartData: any[] = [];
  chartConfig: any = null;

  intervalRefresh: any;

  created() {
    if (screenfull.isEnabled) {
      (screenfull as any).onchange(() => {
        this.isFullscreen = (screenfull as any).isFullscreen;
      });
    }

    this.loadData(this.selectedTab, this.selectedConstraintId, this.selectedInterval, this.selectedRegionId, true);

    this.intervalRefresh = setInterval(() => {
      this.loadData(this.selectedTab, this.selectedConstraintId, this.selectedInterval, this.selectedRegionId, true);
    }, 300 * 1000);
  }

  destroyed() {
    clearInterval(this.intervalRefresh)
  }

  selectTab(tab: string): void {
    this.selectedTab = tab;
    this.loadData(this.selectedTab, this.selectedConstraintId, this.selectedInterval, this.selectedRegionId, false);
  }

  @Watch('selectedConstraintId')
  onSelectedConstraintIdChange(newValue: string, oldValue: string): void {
    this.selectedConstraintId = newValue;
    this.loadData(this.selectedTab, this.selectedConstraintId, this.selectedInterval, this.selectedRegionId, false);
  }

  @Watch('selectedInterval')
  onSelectedIntervalChange(newValue: string, oldValue: string): void {
    this.selectedInterval = newValue;
    this.loadData(this.selectedTab, this.selectedConstraintId, this.selectedInterval, this.selectedRegionId, false);
  }

  @Watch('selectedRegionId')
  onSelectedRegionIdChange(newValue: string, oldValue: string): void {
    this.selectedRegionId = newValue;
    this.loadData(this.selectedTab, this.selectedConstraintId, this.selectedInterval, this.selectedRegionId, false);
  }

  loadData(tab: string, constraintId: string, interval: string, regionId: string, clearDataCache: boolean) {
    this.loadMessage = 'Loading data';
    this.fetchDataAsync(tab, clearDataCache).then(
      (data) => {
        this.data = data;

        let filteredData: any[];
        switch (tab) {
          case this.contributionFactorTab:
            this.constraintIds = Array.from(new Set<string>(data.map(x => x.CID))).sort();
            if (this.constraintIds.indexOf(constraintId) === -1) {
              this.selectedConstraintId = this.constraintIds[0];
              constraintId = this.constraintIds[0];
            }

            filteredData = data.filter(x => x.CID === constraintId);
            filteredData = this.filterIdtByInterval(filteredData, parseInt(interval));
            this.chartData = insertMissingIntervals(filteredData, 'IDT', 300, { RCF: 0 });
            this.chartConfig = this.contributionFactorChartConfig;
            break;

          case this.performanceTab:
            filteredData = data.filter(x => x.RID === regionId);
            filteredData = this.filterIdtByInterval(filteredData, parseInt(interval));
            this.chartData = filteredData;
            this.chartConfig = this.performanceChartConfig;
            break;
        }

        this.loadMessage = '';
      },
      (error) => {
        this.loadMessage = 'No data found';
      });
  }

  async fetchDataAsync(tab: string, clearDataCache: boolean): Promise<any[]> {
    if (clearDataCache) {
      this.dataCache = {};
    }

    let reportName = `FPP_${tab}`;

    if (!this.dataCache[reportName]) {
      let id = await DataManager.addEndPoint('../api/report/' + reportName, undefined, 60000, reportName, undefined, 'yyyy-MM-ddTHH:mm:ss', ['IDT']);
      let data = DataManager.getDynamicView(id, reportName).data();

      // let data: any[];
      // if (tab === this.contributionFactorTab) data = FPP_RESIDUAL_CONTRIBUTION_FACTOR_testData;
      // else if (tab === this.performanceTab) data = FPP_RESIDUAL_PERFORMANCE_testData;
      // data = data.map((x) => ({ ...x, IDT: new Date(Date.parse(x.IDT)) }));

      this.dataCache[reportName] = data;
    }

    return this.dataCache[reportName];
  }

  filterIdtByInterval(data: any[], intervalHours: number): any[] {
    if (data.length === 0) {
      return data;
    }

    let minIdtMillis = data[data.length - 1].IDT.getTime() - intervalHours * 60 * 60 * 1000;
    return data.filter(x => x.IDT.getTime() >= minIdtMillis);
  }

  contributionFactorChartConfig = {
    type: 'serial',
    marginRight: 0,
    marginLeft: 0,
    marginTop: 10,
    marginBottom: 0,
    addClassNames: true,
    panEventsEnabled: false,
    numberFormatter: {
      precision: 5,
      decimalSeparator: '.',
      thousandsSeparator: ','
    },
    valueAxes: [
      {
        id: 'contributionFactor',
        position: "left",
        labelsEnabled: true,
        gridAlpha: 0,
        axisAlpha: 0,
        color: '#8ea9a9',
        title: 'Residual Contribution Factor',
        titleColor: this.purple
      },
    ],
    graphs: [
      {
        valueAxis: 'contributionFactor',
        balloonText: '[[value]]',
        fillAlphas: 1,
        fillColors: this.purple,
        lineColor: this.purple,
        valueField: 'RCF',
        lineThickness: 1,
        type: 'column',
        balloonColor: '#000000',
        balloon: {
          color: '#ffffff',
          fillColor: '#000000',
          showBullet: false
        }
      },
    ],
    categoryAxis: {
      parseDates: true,
      minPeriod: "mm",
      color: '#8ea9a9',
      gridAlpha: 0,
      labelsEnabled: true,
      axisAlpha: 0
    },
    categoryField: "IDT",
    chartCursor: {
      zoomable: false,
      bulletsEnabled: true,
      bulletSize: 12,
      cursorColor: '#000000',
      categoryBalloonFunction: function (value) {
        return formatDate(value, "HH:mm dd MMM");
      }
    }
  };

  performanceChartConfig = {
    type: 'serial',
    marginRight: 0,
    marginLeft: 0,
    marginTop: 10,
    marginBottom: 0,
    addClassNames: true,
    panEventsEnabled: false,
    numberFormatter: {
      precision: 5,
      decimalSeparator: '.',
      thousandsSeparator: ','
    },
    valueAxes: [
      {
        id: 'performance',
        position: "left",
        labelsEnabled: true,
        gridAlpha: 0,
        axisAlpha: 0,
        color: '#8ea9a9',
        title: 'Performance (MWHz)',
        titleColor: 'black'
      }
    ],
    legend: {
      align: 'right',
      position: 'top',
      data: [{ title: 'Raise Performance', color: this.purple }, { title: 'Lower Performance', color: this.lightBlue }],
      equalWidths: true,
    },
    graphs: [
      {
        valueAxis: 'performance',
        balloonText: 'RAISE [[value]]',
        fillAlphas: 1,
        fillColors: this.purple,
        lineColor: this.purple,
        valueField: 'RP',
        lineThickness: 1,
        type: 'column',
        balloonColor: '#000000',
        balloon: {
          color: '#ffffff',
          fillColor: '#000000',
          showBullet: false
        }
      },
      {
        valueAxis: 'performance',
        balloonText: 'LOWER [[value]]',
        fillAlphas: 1,
        fillColors: this.lightBlue,
        lineColor: this.lightBlue,
        valueField: 'LP',
        lineThickness: 1,
        type: 'column',
        balloonColor: '#000000',
        balloon: {
          color: '#ffffff',
          fillColor: '#000000',
          showBullet: false
        }
      }
    ],
    categoryAxis: {
      parseDates: true,
      minPeriod: "mm",
      color: '#8ea9a9',
      gridAlpha: 0,
      labelsEnabled: true,
      axisAlpha: 0
    },
    categoryField: "IDT",
    chartCursor: {
      zoomable: false,
      bulletsEnabled: true,
      bulletSize: 12,
      cursorColor: '#000000',
      categoryBalloonFunction: function (value) {
        return formatDate(value, "HH:mm dd MMM");
      }
    }
  };

  //
  // download
  //

  contributionFactorTableColumns = [
    {
      title: 'Interval Date Time',
      field: 'IDT',
      formatter: (value) => {
        let date = value.IDT || value;
        return formatDate(date, 'dd/MM/yyyy HH:mm');
      },
      sortable: true
    },
    {
      title: 'Constraint ID',
      field: 'CID',
      fieldType: FieldType.String,
      sortable: true
    },
    {
      title: 'Residual Contribution Factor',
      field: 'RCF',
      fieldType: FieldType.Decimal,
      sortable: true
    },
  ];

  performanceTableColumns = [
    {
      title: 'Interval Date Time',
      field: 'IDT',
      formatter: (value) => {
        let date = value.IDT || value;
        return formatDate(date, 'dd/MM/yyyy HH:mm');
      },
      sortable: true
    },
    {
      title: 'Region ID',
      field: 'RID',
      fieldType: FieldType.String,
      sortable: true
    },
    {
      title: 'Raise Performance (MWHz)',
      field: 'RP',
      fieldType: FieldType.Decimal,
      sortable: true
    },
    {
      title: 'Lower Performance (MWHz)',
      field: 'LP',
      fieldType: FieldType.Decimal,
      sortable: true
    },
  ];

  download() {
    let csv = arrayToCsv(this.data, this.selectedTab === this.contributionFactorTab ? this.contributionFactorTableColumns : this.performanceTableColumns);
    downloadCsv(`FPP_${this.selectedTab}_${formatDate(this.data[0].IDT, 'yyyyMMddHHmm')}.csv`, csv);
  }

  //
  // full screen
  //

  isFullscreen = false;

  toggleFullscreen() {
    (screenfull as any).toggle();
    setTimeout(() => {
      if (!this.isFullscreen) {
        this.loadData(this.selectedTab, this.selectedConstraintId, this.selectedInterval, this.selectedRegionId, false);
      }
    }, 2000); // delay gives screen.isFullscreen time to update after screen.toggle()
  }
}


// WEBPACK FOOTER //
// src/views/electricity/fpp/fpp-residual.ts